import { defineNuxtPlugin, useRoute, useRouter } from '#app';
import { openCalltouchWidget } from '~/composables/openCalltouchWidget';
import { yandexRichGoals } from '~/composables/yandexRichGoals';

const SHOW_PARAM = 'showCalltouch';
const READY_TIMEOUT = 6000;
const RETRY_INTERVAL = 400;

interface CalltouchRequestEvent {
  data?: {
    widgetType?: string;
  };
}

interface CalltouchSubscription {
  object: 'request';
  action: 'create';
  callback: (event: CalltouchRequestEvent) => void;
}

type Calltouch = (
  module: 'modules',
  section: 'widgets',
  action: 'subscribeToEvent',
  subscriptions: CalltouchSubscription[],
) => void;

export default defineNuxtPlugin(nuxtApp => {
  const route = useRoute();
  const router = useRouter();
  const { goalsCalltouchSentForm } = yandexRichGoals();

  let running = false;

  const subscribeToCalltouchFormGoal = () => {
    const startedAt = Date.now();

    const attempt = () => {
      const ct = (window as unknown as { ct?: Calltouch }).ct;

      if (typeof ct === 'function') {
        ct('modules', 'widgets', 'subscribeToEvent', [
          {
            object: 'request',
            action: 'create',
            callback: event => {
              if (event?.data?.widgetType === 'promotion') {
                goalsCalltouchSentForm();
              }
            },
          },
        ]);
        return;
      }

      if (Date.now() - startedAt >= READY_TIMEOUT) {
        console.error('Calltouch did not load; the form goal was not subscribed.');
        return;
      }

      window.setTimeout(attempt, RETRY_INTERVAL);
    };

    attempt();
  };

  const stripParam = () => {
    if (!(SHOW_PARAM in route.query)) {
      return;
    }

    const query = { ...route.query };
    delete query[SHOW_PARAM];
    void router.replace({ query });
  };

  const handle = async () => {
    if (running || route.query[SHOW_PARAM] !== 'true') {
      return;
    }

    running = true;
    stripParam();

    try {
      await openCalltouchWidget();
    } finally {
      running = false;
    }
  };

  nuxtApp.hook('app:mounted', () => {
    subscribeToCalltouchFormGoal();
    void handle();
  });

  router.afterEach(() => {
    window.setTimeout(() => {
      void handle();
    }, 0);
  });
});
